/**
 * Base builder user types - extracted to break circular dependencies between
 * builderUsersResponses.ts, permissionsResponses.ts, and usersResponses.ts
 */
import type { BuilderProduct } from "api-types/builders/builderTypes";
import type { FeatureName, FeatureSet } from "common/constants/features";

export type ClientBuilderUser = {
  id: string;
  userId: string;
  builderId: string;
  companyName: string | null;
  title?: string | null;
  externalId?: string | null;
  featureFlags?: FeatureSet | null;
  inviteConfirmedAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
};

export type UserManagementUIVisibilitySettings = {
  showInUserManagementUI?: boolean;
  gateBehindFeatureFlag?: FeatureName;
  gateBehindProduct?: BuilderProduct;
};

type ClientBuilderUserPermissionDefinition = {
  permission: string;
  helpDocs: string;
  canBeResourceScoped?: boolean;
} & UserManagementUIVisibilitySettings;

type ClientBuilderUserPermissionEntry = string | ClientBuilderUserPermissionDefinition;

type ClientBuilderUserPermissionGroupDefinition = {
  group: string;
  permissions: readonly ClientBuilderUserPermissionEntry[];
} & UserManagementUIVisibilitySettings;

function getPermissionEntryCode(permission: ClientBuilderUserPermissionEntry): string {
  return typeof permission === "string" ? permission : permission.permission;
}

function permissionEntrySupportsResourceScopedFallback(permission: ClientBuilderUserPermissionEntry): boolean {
  if (typeof permission === "string") {
    return false;
  }

  return permission.canBeResourceScoped === true;
}

function getPermissionEntryHelpDocs(permission: ClientBuilderUserPermissionEntry): string | undefined {
  if (typeof permission === "string") {
    return undefined;
  }

  return permission.helpDocs;
}

const clientBuilderUserPermissionHelpDocs = {
  VIEW_INTERNAL_IDS: "Allows users to view internal IDs exposed in UI details, such as home plan customer info.",
  ARCHIVE_COMMUNITIES: "Allows users to archive and unarchive any location in the location hierarchy.",
  DEBUG_COMMUNITY_MAP: "Allows users to access community map debugging overlays and diagnostics in map views.",
  VIEW_CLIENT_APPS: "Allows users to view API client app integrations configured for the builder.",
  EDIT_CLIENT_APPS: "Allows users to create, update, or remove API client app integrations and credentials.",
  VIEW_PRICING_VISIBILITY: "Allows users to view which pricing details are visible to different audiences.",
  EDIT_PRICING_VISIBILITY: "Allows users to change pricing visibility rules and who can see pricing information.",
  VIEW_BUILDER_INFO: "Allows users to view core builder profile and account settings information.",
  EDIT_BUILDER_INFO: "Allows users to update core builder profile and account settings information.",
  VIEW_BROCHURE_SETTINGS: "Allows users to open and view the Brochure tab in builder account settings.",
  EDIT_BROCHURE_SETTINGS: "Allows users to edit brochure configuration values in builder account settings.",
  VIEW_SALES_SETTINGS: "Allows users to open and view the Sales tab in builder account settings.",
  EDIT_SALES_SETTINGS: "Allows users to edit sales configuration values in builder account settings.",
  VIEW_STAFF_PERMISSIONS: "Allows users to view roles, policies, and staff permission assignments.",
  EDIT_STAFF_PERMISSIONS: "Allows users to create and edit roles, policies, and staff permission assignments.",
  INVITE_NEW_USERS_TO_BUILDER: "Allows users to invite new users and add them to the builder account.",
  VIEW_LOCATION_SETTINGS: "Allows users to open and view the Location Settings tab in builder account settings.",
  EDIT_LOCATION_SETTINGS: "Allows users to edit location settings values in builder account settings.",
  VIEW_CREDITS: "Allows users to open and view the Credits tab and credit balance/history information.",
  EDIT_CREDITS: "Allows users to perform credit management actions such as credit reload operations.",
  EDIT_CUSTOMER_INFO: "Allows users to edit customer account information and profile details.",
  VIEW_PROCURE: "Allows users to view Estimate data, including mapped codes and resources.",
  EDIT_PROCURE: "Allows users to manage Estimate data, including mapped codes and resources.",
  ADD_ITEM_CODE: "Allows users to create new item codes in Estimate.",
  MODIFY_ITEM_CODE: "Allows users to edit existing item codes in Estimate.",
  DELETE_ITEM_CODE: "Allows users to remove item codes in Estimate.",
  ADD_COST_CODE: "Allows users to create new cost codes in Estimate.",
  MODIFY_COST_CODE: "Allows users to edit existing cost codes in Estimate.",
  DELETE_COST_CODE: "Allows users to remove cost codes in Estimate.",
  ADD_MEASURE_GROUP: "Allows for creating Estimate measure groups in measure-group workflows.",
  MODIFY_MEASURE_GROUP: "Allows for updating existing Estimate measure groups.",
  DELETE_MEASURE_GROUP: "Allows for deleting Estimate measure groups.",
  ADD_RESOURCE_MAPPING: "Allows for creating Estimate resource mappings.",
  MODIFY_RESOURCE_MAPPING: "Allows for updating existing Estimate resource mappings.",
  DELETE_RESOURCE_MAPPING: "Allows for deleting Estimate resource mappings.",
  VIEW_AUTO_RENDERS: "Allows users to view Auto Render jobs and generated render outputs.",
  CREATE_AUTO_RENDERS: "Allows users to create and run Auto Render jobs.",
  VIEW_HOME_PLANS: "Allows users to view home plans and related plan details.",
  CREATE_HOME_PLANS: "Allows users to create new home plans.",
  EDIT_HOME_PLANS: "Allows users to edit existing home plans.",
  DELETE_HOME_PLANS: "Allows users to delete home plans.",
  SET_CATEGORY_ON_HOME_PLANS: "Allows users to assign or change category values on home plans.",
  CHANGE_HOME_PLAN_STATUS: "Allows users to change the status of home plans.",
  ASSIGN_SALES_AGENT_TO_HOME_PLAN: "Allows users to assign or reassign sales agents on home plans.",
  EDIT_HOME_PLAN_DISCOUNT: "Allows users to edit discount values on home plans.",
  EDIT_HOME_PLAN_TOTAL_PRICE_OVERRIDE: "Allows users to override total home plan pricing.",
  MOVE_HOME_PLAN: "Allows users to move a home plan between communities or locations.",
  MOVE_CUSTOMER_PLAN: "Allows users to move a plan submitted directly from a Master Plan public link into a location.",
  EDIT_HOME_PLAN_LOT: "Allows users to change which lot is associated with a home plan.",
  DETACH_HOME_PLAN: "Allows users to detach a home plan from its current assignment or relationship.",
  VIEW_INTEGRATIONS: "Allows users to view integration configurations and sync status.",
  EDIT_INTEGRATIONS: "Allows users to create and edit integration configurations.",
  TRIGGER_INTEGRATION_JOBS: "Allows users to manually trigger integration jobs and sync runs.",
  VIEW_COMMUNITIES: "Allows users to view communities available to their access scope.",
  EDIT_COMMUNITIES: "Allows users to create and edit community details.",
  ASSIGN_SALES_AGENT_TO_COMMUNITY: "Allows users to assign or reassign sales agents to communities.",
  VIEW_CHILD_LOCATIONS: "Allows users to view child locations nested under parent locations.",
  VIEW_COMMUNITY_PLANS: "Allows users to view plans configured for specific communities.",
  EDIT_COMMUNITY_PLANS: "Allows users to add or edit plans configured for specific communities.",
  VIEW_LOT_COMMUNITY_PLANS: "Allows users to view lot-specific community plan assignments.",
  EDIT_LOT_COMMUNITY_PLANS: "Allows users to edit lot-specific community plan assignments.",
  ASSIGN_COMMUNITY_PLAN_TO_LOT: "Allows for assigning community plans to lots in lot-plan assignment flows.",
  VIEW_LOTS: "Allows users to view lot inventory and lot details.",
  EDIT_LOTS: "Allows users to create and edit lot records.",
  EDIT_LOT_DISCOUNT: "Allows users to update lot-level discount values.",
  VIEW_COMMUNITY_MAP: "Allows users to view community maps and map-based lot data.",
  EDIT_COMMUNITY_MAP: "Allows users to edit community maps and map-based lot data.",
  VIEW_MASTER_PLANS: "Allows users to view master plans.",
  EDIT_MASTER_PLANS: "Allows users to create and edit master plans.",
  VIEW_OPTION_CATALOG: "Allows users to view option catalog content and configuration.",
  EDIT_OPTION_CATALOG: "Allows users to create and edit option catalog content and configuration.",
  MERGE_OPTION_CATALOG: "Allows users to merge option sets in the option catalog.",
  REMOVE_OPTION_CATALOG: "Allows users to remove option and set usages from master plans in the option catalog.",
  VIEW_LOCATION_OPTIONS: "Allows users to view location options and sets.",
  EDIT_LOCATION_OPTIONS: "Allows users to edit location options and sets.",
  DOWNLOAD_PLAN_SETS: "Allows users to download lot-specific plan sets without role-template restrictions.",
  DOWNLOAD_PLAN_SETS_IF_SALES_STAFF:
    "Allows assigned sales staff to download built-in plan sets when they are the plan or location sales agent.",
  DOWNLOAD_BUILT_IN_PLAN_SETS: "Allows users to download built-in plan sets regardless of sales-agent assignment.",
  DOWNLOAD_HOME_PLAN_DATA_CSV: "Allows users to export home plan data as CSV.",
  VIEW_SALES_CATEGORIES: "Allows users to view sales categories.",
  EDIT_SALES_CATEGORIES: "Allows users to create and edit sales categories.",
  VIEW_OPERATIONS_MODE: "Allows users to view operations mode settings and state.",
  ASSIGNABLE_AS_SALES_AGENT:
    "Marks users in this role as eligible sales agents (including defaulting the creator as sales staff on new home plans).",
  ASSIGNABLE_AS_SALES_MANAGER: "Marks users in this role as eligible sales managers for assignment workflows.",
  ASSIGNABLE_AS_SALES_TEAM_LEAD: "Marks users in this role as eligible sales team leads for assignment workflows.",
  ALLOWED_AS_SALES_STAFF:
    "Marks users in this role as sales staff so they can appear in sales-staff pickers and related assignments.",
  EDIT_SHOWROOM_INTRO: "Allows users to edit Showroom intro content and messaging.",
  VIEW_STUDIO_FILES: "Allows users to view Studio files available to the builder.",
  EDIT_STUDIO_FILES: "Allows users to create and edit Studio files.",
  ASSIGN_USERS_TO_STUDIO_FILES: "Allows users to manage user assignments for Studio file access.",
} as const;

function createPermissionDefinition(
  permission: string,
  options: Omit<ClientBuilderUserPermissionDefinition, "permission" | "helpDocs"> = {},
): ClientBuilderUserPermissionDefinition {
  const helpDocs = clientBuilderUserPermissionHelpDocs[permission as keyof typeof clientBuilderUserPermissionHelpDocs];

  return {
    permission,
    helpDocs,
    ...options,
  };
}

/**
 * Permission groups - the canonical source of truth for permission organization.
 * Used by both the API types and the UI.
 */
export const clientBuilderUserPermissionGroups = [
  {
    group: "Internal Permissions", // Special permissions group
    showInUserManagementUI: false,
    permissions: [createPermissionDefinition("VIEW_INTERNAL_IDS"), createPermissionDefinition("DEBUG_COMMUNITY_MAP")],
  },
  {
    group: "API & Client Apps",
    gateBehindProduct: "api",
    permissions: [createPermissionDefinition("VIEW_CLIENT_APPS"), createPermissionDefinition("EDIT_CLIENT_APPS")],
  },
  {
    group: "Builder Settings & Admin",
    permissions: [
      createPermissionDefinition("VIEW_PRICING_VISIBILITY"),
      createPermissionDefinition("EDIT_PRICING_VISIBILITY"),
      createPermissionDefinition("VIEW_BUILDER_INFO"),
      createPermissionDefinition("EDIT_BUILDER_INFO"),
      createPermissionDefinition("VIEW_BROCHURE_SETTINGS"),
      createPermissionDefinition("EDIT_BROCHURE_SETTINGS"),
      createPermissionDefinition("VIEW_SALES_SETTINGS"),
      createPermissionDefinition("EDIT_SALES_SETTINGS"),
      createPermissionDefinition("VIEW_STAFF_PERMISSIONS"),
      createPermissionDefinition("EDIT_STAFF_PERMISSIONS"),
      createPermissionDefinition("INVITE_NEW_USERS_TO_BUILDER"),
      createPermissionDefinition("VIEW_LOCATION_SETTINGS"),
      createPermissionDefinition("EDIT_LOCATION_SETTINGS"),
    ],
  },
  {
    group: "Credits",
    permissions: [createPermissionDefinition("VIEW_CREDITS"), createPermissionDefinition("EDIT_CREDITS")],
  },
  {
    group: "Customers",
    permissions: [createPermissionDefinition("EDIT_CUSTOMER_INFO")],
  },
  {
    group: "Estimate",
    gateBehindProduct: "procure",
    permissions: [
      createPermissionDefinition("VIEW_PROCURE"),
      createPermissionDefinition("EDIT_PROCURE"),
      createPermissionDefinition("ADD_ITEM_CODE"),
      createPermissionDefinition("MODIFY_ITEM_CODE"),
      createPermissionDefinition("DELETE_ITEM_CODE"),
      createPermissionDefinition("ADD_COST_CODE"),
      createPermissionDefinition("MODIFY_COST_CODE"),
      createPermissionDefinition("DELETE_COST_CODE"),
      createPermissionDefinition("ADD_MEASURE_GROUP"),
      createPermissionDefinition("MODIFY_MEASURE_GROUP"),
      createPermissionDefinition("DELETE_MEASURE_GROUP"),
      createPermissionDefinition("ADD_RESOURCE_MAPPING"),
      createPermissionDefinition("MODIFY_RESOURCE_MAPPING"),
      createPermissionDefinition("DELETE_RESOURCE_MAPPING"),
    ],
  },
  {
    group: "Auto Renders",
    gateBehindFeatureFlag: "autoRenders",
    gateBehindProduct: "autoRenders",
    permissions: [createPermissionDefinition("VIEW_AUTO_RENDERS"), createPermissionDefinition("CREATE_AUTO_RENDERS")],
  },
  {
    group: "Home Plans",
    permissions: [
      createPermissionDefinition("VIEW_HOME_PLANS"),
      createPermissionDefinition("CREATE_HOME_PLANS"),
      createPermissionDefinition("EDIT_HOME_PLANS"),
      createPermissionDefinition("DELETE_HOME_PLANS"),
      createPermissionDefinition("SET_CATEGORY_ON_HOME_PLANS"),
      createPermissionDefinition("CHANGE_HOME_PLAN_STATUS"),
      createPermissionDefinition("ASSIGN_SALES_AGENT_TO_HOME_PLAN"),
      createPermissionDefinition("EDIT_HOME_PLAN_DISCOUNT"),
      createPermissionDefinition("EDIT_HOME_PLAN_TOTAL_PRICE_OVERRIDE"),
      createPermissionDefinition("MOVE_HOME_PLAN"),
      createPermissionDefinition("MOVE_CUSTOMER_PLAN"),
      createPermissionDefinition("EDIT_HOME_PLAN_LOT"),
      createPermissionDefinition("DETACH_HOME_PLAN"),
    ],
  },
  {
    group: "Integrations",
    permissions: [
      createPermissionDefinition("VIEW_INTEGRATIONS"),
      createPermissionDefinition("EDIT_INTEGRATIONS"),
      createPermissionDefinition("TRIGGER_INTEGRATION_JOBS"),
    ],
  },
  {
    group: "Locations",
    permissions: [
      createPermissionDefinition("VIEW_COMMUNITIES", { canBeResourceScoped: true }),
      createPermissionDefinition("EDIT_COMMUNITIES"),
      createPermissionDefinition("ARCHIVE_COMMUNITIES"),
      createPermissionDefinition("ASSIGN_SALES_AGENT_TO_COMMUNITY"),
      createPermissionDefinition("VIEW_CHILD_LOCATIONS"),
      createPermissionDefinition("VIEW_LOCATION_OPTIONS", { gateBehindFeatureFlag: "nextGenOptionsLocation" }),
      createPermissionDefinition("EDIT_LOCATION_OPTIONS", { gateBehindFeatureFlag: "nextGenOptionsLocation" }),
    ],
  },
  {
    group: "Location Plans",
    permissions: [
      createPermissionDefinition("VIEW_COMMUNITY_PLANS", { canBeResourceScoped: true }),
      createPermissionDefinition("EDIT_COMMUNITY_PLANS"),
    ],
  },
  {
    group: "Lot-Specific Location Plans",
    permissions: [
      createPermissionDefinition("VIEW_LOT_COMMUNITY_PLANS"),
      createPermissionDefinition("EDIT_LOT_COMMUNITY_PLANS"),
      createPermissionDefinition("ASSIGN_COMMUNITY_PLAN_TO_LOT"),
    ],
  },
  {
    group: "Lots",
    permissions: [
      createPermissionDefinition("VIEW_LOTS"),
      createPermissionDefinition("EDIT_LOTS"),
      createPermissionDefinition("EDIT_LOT_DISCOUNT"),
    ],
  },
  {
    group: "Maps",
    permissions: [createPermissionDefinition("VIEW_COMMUNITY_MAP"), createPermissionDefinition("EDIT_COMMUNITY_MAP")],
  },
  {
    group: "Master Plans",
    permissions: [createPermissionDefinition("VIEW_MASTER_PLANS"), createPermissionDefinition("EDIT_MASTER_PLANS")],
  },
  {
    group: "Option Catalog",
    gateBehindFeatureFlag: "nextGenOptions",
    permissions: [
      createPermissionDefinition("VIEW_OPTION_CATALOG"),
      createPermissionDefinition("EDIT_OPTION_CATALOG"),
      createPermissionDefinition("MERGE_OPTION_CATALOG"),
      createPermissionDefinition("REMOVE_OPTION_CATALOG"),
    ],
  },
  {
    group: "Plan Documents & Data",
    permissions: [
      createPermissionDefinition("DOWNLOAD_PLAN_SETS"),
      createPermissionDefinition("DOWNLOAD_PLAN_SETS_IF_SALES_STAFF"),
      createPermissionDefinition("DOWNLOAD_BUILT_IN_PLAN_SETS"),
      createPermissionDefinition("DOWNLOAD_HOME_PLAN_DATA_CSV"),
    ],
  },
  {
    group: "Sales & Marketing",
    permissions: [
      createPermissionDefinition("VIEW_SALES_CATEGORIES"),
      createPermissionDefinition("EDIT_SALES_CATEGORIES"),
      createPermissionDefinition("VIEW_OPERATIONS_MODE"),
      createPermissionDefinition("ASSIGNABLE_AS_SALES_AGENT"),
      createPermissionDefinition("ASSIGNABLE_AS_SALES_MANAGER"),
      createPermissionDefinition("ASSIGNABLE_AS_SALES_TEAM_LEAD"),
      createPermissionDefinition("ALLOWED_AS_SALES_STAFF"),
    ],
  },
  {
    group: "Showroom",
    gateBehindProduct: "showroom",
    permissions: [createPermissionDefinition("EDIT_SHOWROOM_INTRO")],
  },
  {
    group: "Studio Files",
    gateBehindProduct: "studio",
    permissions: [
      createPermissionDefinition("VIEW_STUDIO_FILES"),
      createPermissionDefinition("EDIT_STUDIO_FILES"),
      createPermissionDefinition("ASSIGN_USERS_TO_STUDIO_FILES"),
    ],
  },
] as const satisfies readonly ClientBuilderUserPermissionGroupDefinition[];

type ClientBuilderUserPermissionEntryValue = (typeof clientBuilderUserPermissionGroups)[number]["permissions"][number];

type PermissionFromEntry<TPermissionEntry extends ClientBuilderUserPermissionEntryValue> = TPermissionEntry extends {
  permission: infer TPermission extends string;
}
  ? TPermission
  : TPermissionEntry extends string
  ? TPermissionEntry
  : never;

export type ClientBuilderUserPermission = PermissionFromEntry<ClientBuilderUserPermissionEntryValue>;

export const permissionHelpDocs = Object.fromEntries(
  clientBuilderUserPermissionGroups.flatMap((group) =>
    group.permissions.flatMap((permission) => {
      const helpDocs = getPermissionEntryHelpDocs(permission);
      if (!helpDocs) {
        return [];
      }

      return [[getPermissionEntryCode(permission), helpDocs] as const];
    }),
  ),
) as Partial<Record<ClientBuilderUserPermission, string>>;

export type ClientBuilderUserPermissionGroup = {
  group: string;
  permissions: ClientBuilderUserPermission[];
} & UserManagementUIVisibilitySettings;

// Derive flat array from groups for backwards compatibility with zod schemas and type derivation
export const clientBuilderUserPermissions = clientBuilderUserPermissionGroups.flatMap((g) =>
  g.permissions.map((permission) => getPermissionEntryCode(permission) as ClientBuilderUserPermission),
);

export const clientBuilderUserPermissionsWithResourceScopedFallback = clientBuilderUserPermissionGroups.flatMap((g) =>
  g.permissions
    .filter((permission) => permissionEntrySupportsResourceScopedFallback(permission))
    .map((permission) => getPermissionEntryCode(permission) as ClientBuilderUserPermission),
);

export type ResourceScopedPermissionResourceType = "communities" | "community_plans";
export type ResourceScopedPermissionKind = "view" | "edit";

export function getResourceScopedPermission(args: {
  resourceType: ResourceScopedPermissionResourceType;
  kind: ResourceScopedPermissionKind;
}): ClientBuilderUserPermission {
  if (args.resourceType === "communities") {
    return args.kind === "view" ? "VIEW_COMMUNITIES" : "EDIT_COMMUNITIES";
  }

  return args.kind === "view" ? "VIEW_COMMUNITY_PLANS" : "EDIT_COMMUNITY_PLANS";
}

export function getResourceScopedViewPermission(
  resourceType: ResourceScopedPermissionResourceType,
): ClientBuilderUserPermission {
  return getResourceScopedPermission({ resourceType, kind: "view" });
}

export const clientBuilderUserPermissionValues = ["ALLOW", "DENY", "DEFER"] as const;
type ClientBuilderUserPermissionValue = (typeof clientBuilderUserPermissionValues)[number];
export type ClientBuilderUserPermissions = Record<ClientBuilderUserPermission, ClientBuilderUserPermissionValue>;

/**
 * Permission display names - for permissions that need a custom display name
 * different from the auto-generated one (e.g., "VIEW_PROCURE" -> "View Estimate")
 */
export const permissionDisplayNames: Partial<Record<ClientBuilderUserPermission, string>> = {
  VIEW_PROCURE: "View Estimate",
  EDIT_PROCURE: "Edit Estimate",
  ARCHIVE_COMMUNITIES: "Archive and unarchive locations",
  MERGE_OPTION_CATALOG: "Merge option sets",
  REMOVE_OPTION_CATALOG: "Remove option usages",
  VIEW_LOCATION_OPTIONS: "View Options Tab",
  EDIT_LOCATION_OPTIONS: "Edit Options Tab",
};
